import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { isAmountPreset } from '../../../../lib/amountPresets';
import { Formik } from 'formik';
import { useCallback } from 'react';
import { useCallbacks } from '../../../../context/callbackProvider';
import { lifecycleContextFromForm } from '../../../../lib/swapLifecycle';
import FormTelemetry from './FormTelemetry';
import AmountPresetController from './AmountPresetController';
import AtomicBatchDiscovery from './AtomicBatchDiscovery';
/**
 * The only place `<Formik>` is instantiated for SwapFormValues
 * (tests/swap-form-owners.test.mjs). Owning the form here means every
 * surface gets the same three things without remembering them: the
 * widget_flow journey (FormTelemetry), the `[data-ls-form]` interaction
 * boundary, and one `form_submitted` per Formik submission, which is what
 * starts a new telemetry journey and lets `swap_created` attach to it.
 */
export default function SwapForm({ mode, submitPath, submitAction = 'submit', onSubmit, children, ...formik }) {
    const { onSwapLifecycle } = useCallbacks();
    const handleSubmit = useCallback((values, helpers) => {
        if (isAmountPreset(values.amountMode) && (values.amountPresetPending || values.amountPresetError))
            return Promise.resolve();
        onSwapLifecycle({
            step: 'form_submitted',
            stage: 'form',
            outcome: 'started',
            path: submitPath,
            action: submitAction,
            ...lifecycleContextFromForm(values),
        });
        // Always hand Formik a promise so isSubmitting resets even for a synchronous onSubmit.
        return Promise.resolve(onSubmit(values, helpers));
    }, [onSwapLifecycle, onSubmit, submitPath, submitAction]);
    return (_jsx(Formik, { ...formik, onSubmit: handleSubmit, children: props => (
        // display:contents keeps layout. captureWidgetInteraction resolves the
        // boundary with Element.closest('[data-ls-form]'), so portaled drawers
        // rendered outside this subtree stay outside the form.
        _jsxs("div", { "data-ls-form": mode, className: "contents", children: [_jsx(FormTelemetry, { mode: mode }), _jsx(AmountPresetController, {}), _jsx(AtomicBatchDiscovery, {}), typeof children === 'function' ? children(props) : children] })) }));
}
